import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguageStore, useAuthStore } from '../store';
import { useTranslation } from '../i18n/translations';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ShieldAlert, Trash2, Save, User } from 'lucide-react';

export const AccountPage = () => {
  const { language } = useLanguageStore();
  const { t } = useTranslation(language);
  const { user, logout, refreshUser } = useAuthStore();
  const navigate = useNavigate();
  const API = process.env.REACT_APP_BACKEND_URL || 'https://fedyk-furniture-backend.onrender.com';

  const l = t.account || {};

  // Локальные словари для уведомлений и системных сообщений
  const messages = {
    profileUpdated: {
      en: 'Profile updated successfully',
      ru: 'Профиль успешно обновлен',
      nl: 'Profiel succesvol bijgewerkt',
      ro: 'Profilul a fost actualizat cu succes'
    },
    sessionExpired: {
      en: 'Session expired. Please log in again.',
      ru: 'Сессия истекла. Пожалуйста, войдите снова.',
      nl: 'Sessie verlopen. Log opnieuw in.',
      ro: 'Sesiunea a expirat. Vă rugăm să vă autentificați din nou.'
    },
    updateFailed: {
      en: 'Failed to update profile',
      ru: 'Не удалось обновить профиль',
      nl: 'Profiel bijwerken mislukt',
      ro: 'Actualizarea profilului a eșuat'
    },
    serverError: {
      en: 'Server connection error. Please try again.',
      ru: 'Ошибка соединения с сервером. Пожалуйста, попробуйте еще раз.',
      nl: 'Verbindingsfout met server. Probeer het opnieuw.',
      ro: 'Eroare de conexiune la server. Vă rugăm să încercați din nou.'
    },
    deleteFailed: {
      en: 'Failed to process deletion request',
      ru: 'Не удалось обработать запрос на удаление',
      nl: 'Verwijderingsverzoek verwerken mislukt',
      ro: 'Procesarea cererii de ștergere a eșuat'
    },
    officePlaceholder: {
      en: 'Box 4 (opt.)',
      ru: 'Офис / апарт. (опц.)',
      nl: 'Box 4 (opt.)',
      ro: 'Caseta 4 (opț.)'
    }
  };

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    company_name: '',
    vat_number: '',
    company_street: '',
    company_office: '',
    company_city: '',
    company_postcode: '',
    company_country: ''
  });
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        phone: user.phone || '',
        company_name: user.company_name || '',
        vat_number: user.vat_number || '',
        company_street: user.company_street || '',
        company_office: user.company_office || '',
        company_city: user.company_city || '',
        company_postcode: user.company_postcode || '',
        company_country: user.company_country || ''
      });
    } else {
      navigate(`/${language}/login`);
    }
  }, [user, language, navigate]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const getAuthToken = () => {
    try {
      const authData = localStorage.getItem('fedyk-auth');
      if (authData) {
        const parsed = JSON.parse(authData);
        return parsed?.state?.token || parsed?.state?.access_token || null;
      }
    } catch (e) {
      console.error("Error reading auth token", e);
    }
    return null;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      const token = getAuthToken();
      if (!token) throw new Error(messages.sessionExpired[language] || 'Session expired. Please log in again.');

      const res = await fetch(`${API}/api/auth/update-profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });
      
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || messages.updateFailed[language] || 'Failed to update profile');
      
      setFormData({ ...formData });

      if (refreshUser) {
        await refreshUser();
      }
      
      toast.success(messages.profileUpdated[language] || 'Profile updated successfully');
    } catch (error) {
      toast.error(error.message || messages.serverError[language] || 'Server connection error. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteRequest = async () => {
    if (!window.confirm(l.deleteConfirm || 'Are you absolutely sure you want to request account deletion?')) {
      return;
    }
    setIsDeleting(true);
    try {
      const token = getAuthToken();
      if (!token) throw new Error(messages.sessionExpired[language] || 'Session expired. Please log in again.');

      const res = await fetch(`${API}/api/auth/delete-request`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ language })
      });
      
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || messages.deleteFailed[language] || 'Failed to process deletion request');
      
      toast.success(l.deleteSuccess || 'Verification link sent. Please check your email.');
      logout();
      navigate(`/${language}`);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsDeleting(false);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#F4F2EE] py-20 px-4 md:px-8">
      <div className="max-w-2xl mx-auto bg-white border border-border p-8 md:p-12">
        
        <div className="border-b border-border pb-6 mb-8 flex items-center justify-between">
          <div>
            <span className="text-[11px] tracking-[0.25em] text-muted-foreground uppercase block mb-1">
              {l.eyebrow || 'Cabinet'}
            </span>
            <h1 className="text-2xl md:text-3xl font-heading font-light text-foreground">
              {l.title || 'My Account'}
            </h1>
          </div>
          <div className="p-3 bg-[#FAFAFA] rounded-full text-muted-foreground">
            <User className="w-6 h-6" />
          </div>
        </div>

        <form onSubmit={handleSave} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label htmlFor="first_name">{l.firstName || 'First Name'}</Label>
              <Input id="first_name" name="first_name" value={formData.first_name} onChange={handleChange} className="h-12 mt-2" required />
            </div>
            <div>
              <Label htmlFor="last_name">{l.lastName || 'Last Name'}</Label>
              <Input id="last_name" name="last_name" value={formData.last_name} onChange={handleChange} className="h-12 mt-2" required />
            </div>
          </div>

          <div>
            <Label htmlFor="email">{l.emailAddress || 'Email Address'}</Label>
            <Input id="email" value={user.email} className="h-12 mt-2 bg-[#FAFAFA] text-muted-foreground cursor-not-allowed" disabled />
            <p className="text-[11px] text-muted-foreground mt-1">
              {l.emailHint || 'To change your email, please contact atelier support.'}
            </p>
          </div>

          <div>
            <Label htmlFor="phone">{l.phone || 'Phone Number'}</Label>
            <Input id="phone" name="phone" value={formData.phone} onChange={handleChange} className="h-12 mt-2" />
          </div>

          {user.is_b2b && (
            <div className="p-6 bg-[#FAFAFA] border border-border space-y-6 mt-4">
              <span className="text-[10px] tracking-[0.2em] text-muted-foreground uppercase block font-semibold">
                {l.tradeDetails || 'Trade Partner Details'}
              </span>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="company_name">{l.companyName || 'Company Name'}</Label>
                  <Input id="company_name" name="company_name" value={formData.company_name} onChange={handleChange} className="h-12 mt-2 bg-white" />
                </div>
                <div>
                  <Label htmlFor="vat_number">{l.vatNumber || 'VAT Number'}</Label>
                  <Input id="vat_number" name="vat_number" value={formData.vat_number} onChange={handleChange} className="h-12 mt-2 bg-white" />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="col-span-2">
                  <Label htmlFor="company_street">{l.streetNumber || 'Street & Number'}</Label>
                  <Input id="company_street" name="company_street" value={formData.company_street} onChange={handleChange} className="h-12 mt-2 bg-white" placeholder="Rue de la Régence 10" required={user.is_b2b} />
                </div>
                <div>
                  <Label htmlFor="company_office">{l.officeSuite || 'Office / Suite'}</Label>
                  <Input id="company_office" name="company_office" value={formData.company_office} onChange={handleChange} className="h-12 mt-2 bg-white" placeholder={messages.officePlaceholder[language] || 'Box 4 (opt.)'} />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="col-span-2">
                  <Label htmlFor="company_city">{l.city || 'City'}</Label>
                  <Input id="company_city" name="company_city" value={formData.company_city} onChange={handleChange} className="h-12 mt-2 bg-white" placeholder="Brussels" required={user.is_b2b} />
                </div>
                <div>
                  <Label htmlFor="company_postcode">{l.postalCode || 'Postal Code'}</Label>
                  <Input id="company_postcode" name="company_postcode" value={formData.company_postcode} onChange={handleChange} className="h-12 mt-2 bg-white" placeholder="1000" required={user.is_b2b} />
                </div>
              </div>

              <div>
                <Label htmlFor="company_country">{l.country || 'Country'}</Label>
                <Input id="company_country" name="company_country" value={formData.company_country} onChange={handleChange} className="h-12 mt-2 bg-white" placeholder="Belgium" required={user.is_b2b} />
              </div>

              <div>
                <Label>{l.profession || 'Professional Role'}</Label>
                <div className="h-12 mt-2 flex items-center px-3 border border-input bg-white text-sm text-muted-foreground capitalize">
                  {t.auth?.[user.role] || user.role}
                </div>
              </div>
            </div>
          )}

          <div className="pt-4">
            <Button type="submit" className="w-full h-12 bg-black text-white uppercase text-xs tracking-[0.2em]" disabled={isSaving}>
              <Save className="w-4 h-4 mr-2" />
              {isSaving ? (l.saving || 'Saving Changes...') : (l.saveChanges || 'Save Changes')}
            </Button>
          </div>
        </form>

        <div className="mt-12 pt-8 border-t border-border">
          <div className="flex items-start gap-4 p-4 border border-red-200 bg-red-50/30">
            <ShieldAlert className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-sm font-medium text-red-900">{l.dangerZone || 'Danger Zone'}</h3>
              <p className="text-xs text-red-700/80 mt-1">
                {l.dangerDesc || 'Once you request account deletion, we will send a confirmation link to your email. Clicking it will permanently purge all your data.'}
              </p>
              <Button type="button" onClick={handleDeleteRequest} disabled={isDeleting} className="mt-4 h-9 bg-red-600 hover:bg-red-700 text-white text-xs tracking-wider uppercase">
                <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                {isDeleting ? (l.deleting || 'Processing...') : (l.deleteBtn || 'Delete My Account')}
              </Button>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default AccountPage;
